import {
  addClass,
  bindEvents,
  getElement,
  onDrag,
  removeClass,
  removeFromArray,
  scrollY,
  Sentinel,
  sortedArrayDiff,
  throttle,
  toggleClass
} from "./common";
import { DefaultMode } from "./uploaderViewModes";
import EventEmitter from "./EventEmitter";
import AddButton from "./AddButton";
import EmptyView from "./EmptyView";
import ExtendableObject from "./ExtendableObject";
import View from "./View";
import FileView from "./FileView";
import ImageFileView from "./ImageFileView";
import Placeholder from "./PlaceholderView";

//=============================================================================
const Scroller = ExtendableObject.extend(
  /** @lends Scroller.prototype */
  {
    /**
     * Helper for continuous scrolling
     * @constructs
     * @extends ExtendableObject
     */
    constructor() {
      this.speed = 0; // px per sec
      this.accumulator = 0;
      this.timeout = 0;
      this.lastFired = 0;
      this.boundTick = t => { this.tick(t); };
    },

    /**
     * Set scrolling speed. Positive is down.
     * @param {number} speed
     */
    set(speed) {
      this.speed = speed;
      if(speed === 0) this.stop();
      else this.start();
    },

    /**
     * Stops the animation loop
     * @private
     */
    start() {
      if(this.timeout) return;
      this.timeout = requestAnimationFrame(this.boundTick);
    },

    /**
     * Runs the animation loop
     * @private
     */
    stop() {
      cancelAnimationFrame(this.timeout);
      this.timeout = 0;
      this.lastFired = 0;
    },

    /**
     * Animation loop callback
     * @param {number} t - current time in ms
     * @private
     */
    tick(t) {
      this.timeout = requestAnimationFrame(this.boundTick);
      if(this.lastFired === 0) {
        this.lastFired = t;
        return;
      }
      this.accumulator += this.speed * (t - this.lastFired) / 1000;
      this.lastFired = t;

      const delta = this.accumulator | 0; // round towards zero
      this.accumulator -= delta;
      window.scrollBy(0, delta);
    }
  });
//=============================================================================
export const PositionAnimation = ExtendableObject.extend(
  /** @lends PositionAnimation.prototype */
  {
    /**
     * Helper for animating a set of {@link: ItemView}s from one place to another using CSS transitions.
     * @param {ItemView[]} views
     */
    constructor(views) {
      this.views = views;
      this.positions = null;
    },

    /**
     * Record current positions. To be called before reordering elements in DOM.
     */
    recordPositions() {
      this.positions = this.views.map(i => i.getPositionReferenceAnimated());
    },

    /**
     * Animate items from recorded positions to current ones.
     * Achieved through setting `transition` and `transform: translate3d` CSS properties.
     */
    run() {
      if(!this.positions) throw new Error("PositionAnimation: recordPositions needs to be called before run");

      if(!this.positions.length) return;

      // filter items which moved
      // and save transforms to original positions
      const movedItems = this.views.filter((view, i) => {
        const [dx, dy] = view.getPositionDeltaAnimated(this.positions[i]);
        if(dx === 0 && dy === 0) return false;
        view.css({
          transition: "none",
          transform: `translate3d(${-dx}px, ${-dy}px, 0)`
        });
        return true;
      });

      if(!movedItems.length) return; // nothing actually moved

      movedItems[movedItems.length - 1].elements[0].getBoundingClientRect(); // update

      // animate items to current positions
      movedItems.forEach(i => { i.css({ transition: null, transform: null }); });
    }
  }
);
//=============================================================================
const UploaderView = View.extend(
  /** @lends UploaderView.prototype */
  {
    /**
     * View for the uploader
     * @param {UploaderModel} model
     * @param {object} opts
     * @constructs
     * @extends View
     */
    constructor(model, opts) {
      View.call(this, model, opts);

      /**
       * @protected
       * @type {HTMLElement}
       */
      this.el = getElement(this.opts.el);
      if(!this.el) throw new Error("Uploader: el must be an element or a valid id");

      if(this.opts.additionalClass) this.opts.additionalClass.split(/\s+/).forEach(c => addClass(this.el, c));
      if (this.opts.enable_capture) addClass(this.el, "uploader-capture");

      // uploading

      /**
       * @protected
       * @type {HTMLInputElement}
       */
      this.fileInput = document.createElement("input");

      // append required by IE
      this.fileInput.style.display = "none";
      document.body.appendChild(this.fileInput);

      this.fileInput.type = "file";
      this.fileInput.multiple = this.opts.max > 1;
      if (this.opts.mimes) this.fileInput.accept = this.opts.mimes;

      // modes & events

      /**
       * @protected
       * @type {UploaderViewMode}
       */
      this.mode = new DefaultMode(this);

      this.constructDOMAndBindEvents();

      // item views

      /**
       * @protected
       * @type {Array}
       */
      this.items = [];
      this.handleItemsUpdated();

      /**
       * @protected
       * @type {object|null}
       */
      this.dragOperation = null;

      this.scroller = new Scroller();
    },

    /**
     * Handles pointer move during actual dragging
     * @private
     * @param {Event} evt
     */
    handleDrag(evt) {
      if(!this.dragOperation) return; // the call may be throttled and delayed after drop

      const touch = this.dragOperation.touch;

      let evtX, evtY;

      if(evt.type === "scroll") {
        // we don't get pointer coordinates on scroll - reuse the last ones
        evtX = this.dragOperation.lastX;
        evtY = this.dragOperation.lastY;
      } else {
        evtX = this.dragOperation.lastX = (touch ? evt.touches[0] : evt).clientX;
        evtY = this.dragOperation.lastY = (touch ? evt.touches[0] : evt).clientY;
      }

      const dx = Math.round(evtX - this.dragOperation.origX);
      const dy = Math.round(evtY - this.dragOperation.origY + this.dragOperation.pageY);

      const { item, placeholder } = this.dragOperation;

      item.css({
        transform: `translate(${dx}px, ${dy}px)`
      });

      const items = [placeholder, ...this.items];

      let closest = null;
      let minDist = Infinity;

      for(let i = 0; i < items.length; i++) {
        const it = items[i];
        if(it === item) continue;
        const dist = it.distanceSqr(evtX, evtY);
        if(dist < minDist) {
          closest = it;
          minDist = dist;
        }
      }

      if(closest !== placeholder) {
        this.model.insertBefore(item.model, closest.model);
      }

      // TODO: test the scrolling behavior
      const scrollThreshold = 0.2; // top/bottom fraction of screen, where autoscroll activates
      const scrollSpeed = 350; // maximum speed in px/s
      const relY = evtY / window.innerHeight;
      if(relY < scrollThreshold) {
        this.scroller.set(-scrollSpeed * (1 - relY / scrollThreshold));
      } else if(relY > (1 - scrollThreshold)) {
        this.scroller.set(scrollSpeed * (relY - 1 + scrollThreshold) / scrollThreshold);
      } else {
        this.scroller.set(0);
      }
    },

    /**
     * Handles end of actual dragging
     * @private
     */
    handleDragEnd() {
      const { item, placeholder } = this.dragOperation;

      item.css({
        transform: null,
        transition: null,
        position: null,
        left: null,
        top: null,
        width: null,
        height: null
      });

      this.el.getBoundingClientRect(); // update

      item.markDragged(false);

      placeholder.destroy();

      this.dragOperation = null;
      this.scroller.set(0);

      this.model.uploadSort();
    },

    /**
     * Factory method for placeholder view creation
     * @protected
     */
    createPlaceholderView: function(model) {
      return new Placeholder(model, this.opts);
    },

    /**
     * Initialize actual dragging. To be called from mode method (see {@link UploaderViewMode})
     * @param evt
     */
    initDrag(evt) {
      const { target: item, touch } = evt;

      const prevPos = item.getPositionReference();

      addClass(this.el, "drag");

      const rect = item.getBBox();

      item.markDragged(true);
      item.css({
        position: "absolute",
        left: "0px",
        top: "0px",
        width: Math.round(rect.right - rect.left) + "px",
        height: Math.round(rect.bottom - rect.top) + "px",
      });

      const [dx, dy] = item.getPositionDelta(prevPos);

      this.dragOperation = {
        origX: evt.x,
        origY: evt.y + scrollY(),
        lastX: evt.x,
        lastY: evt.y,
        pageY: scrollY(),
        item,
        touch
      };

      this.dragOperation.origX += dx;
      this.dragOperation.origY += dy;

      this.dragOperation.placeholder = this.createPlaceholderView(item.model);
      this.dragOperation.placeholder.insertBeforeView(item);

      this.handleDrag(evt.event);

      const move = throttle(evt => this.handleDrag(evt));

      // scroll handler has to be separate from move, so scrolls don't throttle mousemoves
      const scroll = throttle(evt => {
        if(!this.dragOperation) return; // the call may be throttled and delayed after drop
        this.dragOperation.pageY = scrollY();
        this.handleDrag(evt)
      });

      const end = () => {
        if(touch) {
          document.body.style.overflow = "";
          document.body.style.position = "";
        }
        unbindEvents();
        this.handleDragEnd();
        this.dragOperation = null;
        removeClass(this.el, "drag");
      };

      let unbindEvents;
      if(touch) {
        unbindEvents = bindEvents(document, {
          touchmove: move,
          scroll: scroll,
          touchend: end,
          touchcancel: end
        });
        // prevent scrolling
        // event.preventDefault() does not work, when attached during dragging
        // TODO: needs testing
        document.body.style.overflow = "hidden";
        document.body.style.position = "relative";
      } else {
        unbindEvents = bindEvents(document, {
          mousemove: move,
          scroll: scroll,
          mouseup: end
        });
      }
    },

    /**
     * Handle dragstart (mouse + touch) event from an item view
     * @param {object} evt
     * @private
     */
    handleItemDragStart(evt) {
      if(this.items.length === 1) return;
      this.mode.drag(evt);
    },

    /** */
    handleItemClick(evt) {
      return this.mode.click(evt);
    },

    /**
     * Create a a view for given file model and listen to its events
     * @param {FileModel} model
     * @returns {FileView}
     */
    initItemView(model) {
      const view = this.createItemView(model);

      this.listenTo(view, "dragstart", evt => this.handleItemDragStart(evt));
      this.listenTo(view, "dragcancel", evt => { /*TODO: user scrolled using touch, show touch drag hint like google maps do*/ });
      this.listenTo(view, "click", evt => this.handleItemClick(evt));
      this.listenTo(view, "all", evt => this.emit(`item:${evt.type}`, evt));

      return view;
    },

    /**
     * Handles `files_changed` event from the model
     * @private
     */
    handleItemsUpdated() {
      // toggle visibilities and classes first, so animations get the right measurements
      const cnt = this.model.items.length;
      const isFull = this.model.isFull();

      if(cnt) {
        this.emptyView.hide();
        this.addBtn.show();
      } else {
        this.emptyView.show();
        this.addBtn.hide();
      }

      if(isFull) this.addBtn.hide();

      toggleClass(this.el, "full", isFull);
      toggleClass(this.el, "empty", cnt === 0);
      toggleClass(this.el, "one", cnt === 1);
      toggleClass(this.el, "more", cnt > 1 && !isFull);


      const idSort = (m1, m2) => m1.id - m2.id;

      const itemsById = {};
      this.items.forEach(i => { itemsById[i.model.id] = i; });

      const prevModels = this.items.map(i => i.model).sort(idSort);
      const currModels = this.model.items.slice().sort(idSort);

      const { added, removed, preserved } = sortedArrayDiff(prevModels, currModels, idSort);

      added.forEach(m => { itemsById[m.id] = this.initItemView(m); });

      // all elements, which we want to animate
      const animatedItems = preserved.map(m => itemsById[m.id]);

      // animate add button, but only if some items already present (looks better)
      // actually don't, it looks good only when not wrapped
      // if(this.items.length) animatedItems.push(this.addBtn);

      if(this.dragOperation) removeFromArray(animatedItems, this.dragOperation.item);

      const animation = new PositionAnimation(animatedItems);
      animation.recordPositions();

      // update item views list
      this.items = this.model.items.map(m => itemsById[m.id]);

      // sort items
      if(this.items.length) {
        let last = this.items[0];
        if(!last.isMounted) last.insertBefore(this.itemsEl);

        for(let i = 1; i < this.items.length; i++) {
          if(this.dragOperation && this.dragOperation.item === last && this.dragOperation.placeholder) {
            this.dragOperation.placeholder.insertAfterView(last);
            last = this.dragOperation.placeholder;
          }
          const item = this.items[i];
          item.insertAfterView(last);
          last = item;
        }

        if(this.dragOperation && this.dragOperation.item === last && this.dragOperation.placeholder) {
          this.dragOperation.placeholder.insertAfterView(last);
        }
      }

      // destroy removed ones
      // removed.forEach(m => { itemsById[m.id].destroy(); }); // this happens in the views
      EventEmitter.all(removed, "destroy", () => { animation.run(); });
    },

    /**
     * Factory method for item view creation
     * @param {FileModel} model
     * @returns {FileView}
     */
    createItemView(model) {
      if(/^image\//.test(model.getMIME())) return new ImageFileView(model, this.opts);

      return new FileView(model, this.opts);
    },

    /**
     * Open file input
     */
    openFileInput() {
      // this.fileInput.accept = "";
      this.fileInput.removeAttribute("capture");
      this.fileInput.click();
    },

    /**
     * Open camera or open file input
     */
    capturePhoto() {
      // this.fileInput.accept = "image/*";
      this.fileInput.capture = "camera";
      this.fileInput.click();
    },

    /**
     * Set mode
     * @param {function} newModeClass - a constructor which inherits from {@link UploaderViewMode}
     */
    setMode(newModeClass) {
      this.mode.modeLeave();
      const newMode = new newModeClass(this);
      newMode.modeEnter();
      this.mode = newMode;
    },

    /**
     * Handle files added from a DOM event (change, drop)
     * @param evt
     */
    handleSelect(evt) {
      this.model.addFiles(evt.target.files || evt.dataTransfer.files);
    },

    /**
     * Gets an element with given id and standard `_<id>` suffix
     * @param {string} id
     * @returns {HTMLElement | null}
     * @protected
     */
    getElement(id) {
      return document.getElementById(`${id}_${this.id}`);
    },

    /**
     * Default options factory
     * @protected
     * @returns {object}
     */
    getDefaultOptions() {
      return {};
    },

    /**
     * Handle drag enter
     * @param {Event} evt
     * @protected
     */
    handleDragEnter(evt) {
      evt.dataTransfer.dropEffect = this.model.isFull() ? "none" : "copy";
      addClass(this.el, "drag_over");
      evt.preventDefault();
    },

    /**
     * Handle drag leave
     * @param {Event} evt
     * @protected
     */
    handleDragLeave(evt) {
      removeClass(this.el, "drag_over");
      evt.preventDefault();
    },

    /**
     * Handle drop event
     * @param {Event} evt
     * @protected
     */
    handleDrop(evt) {
      removeClass(this.el, "drag_over");
      this.hilite(false);
      evt.preventDefault();
      this.mode.drop(evt);
    },

    /**
     * Toggle visual highlight
     * @param {boolean} state
     * @protected
     */
    hilite(state) {
      toggleClass(this.el, "drop_hl", state);
    },

    /**
     * Construct DOM and bind events
     * @private
     */
    constructDOMAndBindEvents() {
      // prepare ##place## sentinels
      const itemsSentinel = new Sentinel();
      const addBtnSentinel = new Sentinel();
      const emptySentinel = new Sentinel();

      // render html
      this.el.innerHTML = this.opts.template.parse("base", {
        items: itemsSentinel.string,
        add_button: addBtnSentinel.string,
        empty: emptySentinel.string,
        id: this.id
      });

      // grab references to elements
      this.itemsEl = itemsSentinel.getReference();
      const addBtnEl = addBtnSentinel.getReference();
      const emptyEl = emptySentinel.getReference();

      // error if elements not present
      if(!this.itemsEl) throw new Error("Uploader: template did not specify ##items## place");
      if(!addBtnEl) throw new Error("Uploader: template did not specify ##add_button## place");
      if(!emptyEl) throw new Error("Uploader: template did not specify ##empty## place");

      // place respective views and bind listeners
      this.addBtn = new AddButton(this.model, this.opts);
      this.addBtn.insertBefore(addBtnEl);
      this.listenTo(this.addBtn, "click", () => { this.mode.add(); });
      this.listenTo(this.addBtn, "capture", () => { this.mode.capture(); });

      this.emptyView = new EmptyView(this.model, this.opts);
      this.emptyView.insertBefore(emptyEl);
      this.listenTo(this.emptyView, "click", () => { this.mode.add(); });
      this.listenTo(this.emptyView, "capture", () => { this.mode.capture(); });

      this.fileInput.addEventListener("change", evt => {
        this.handleSelect(evt);
        this.fileInput.value = null; // so change event gets fired next time
      });

      onDrag({
        el: document.documentElement,
        enter: () => { this.hilite(true); },
        leave: () => { this.hilite(false); }
      });

      onDrag({
        el: this.el,
        enter: evt => { this.handleDragEnter(evt); },
        leave: evt => { this.handleDragLeave(evt); }
      });

      this.el.addEventListener("dragover", evt => {
        evt.dataTransfer.dropEffect = this.model.isFull() ? "none" : "copy";
        evt.stopPropagation();
        evt.preventDefault();
      });
      this.el.addEventListener("drop", evt => { this.handleDrop(evt); });

      this.mode.modeEnter();

      this.listenTo(this.model, "files_changed", () => { this.handleItemsUpdated(); });
    }
  });

export default UploaderView;
